import { ssoClient } from '@better-auth/sso/client';
import { createAuthClient } from 'better-auth/client';
import { genericOAuthClient } from 'better-auth/client/plugins';

import config from '../config.js';
import { validateReturnToUrl } from './validate-return-to-url.js';

const createBetterAuthClient = () =>
	createAuthClient({
		baseURL:
			config.auth.baseUrl ??
			config.hosts.api.replace('/api/', '').replace('/api', ''),
		plugins: [
			ssoClient(),
			genericOAuthClient(), // Support for Auth0 as OIDC provider
		],
		// Ensure credentials are always included for cross-origin cookie handling
		fetchOptions: {
			credentials: 'include',
		},
	});

/**
 * Better Auth client instance for authentication via Auth0.
 */
let authClient: ReturnType<typeof createBetterAuthClient> | null =
	globalThis._authClient ?? null;

if (import.meta.hot) {
	import.meta.hot.on('vite:beforeUpdate', () => {
		globalThis._authClient = authClient;
	});
}

export function getAuthClient() {
	if (!authClient) {
		authClient = createBetterAuthClient();
	}

	return authClient;
}

/**
 * Triggers a logout and redirects to the SAML IdP logout URL if it is configured.
 * or reloads the page if no SAML IdP logout URL is configured.
 *
 * @returns A promise which resolves with undefined once logged out, however the page does get redirected or reloaded.
 */
export async function logout(redirectTo?: string) {
	const returnUrl = validateReturnToUrl(redirectTo);
	const authClient = getAuthClient();

	try {
		// Sign out from better-auth (clears session cookie)
		await authClient.signOut();
	} catch (error) {
		console.error('Logout error:', error);
	}

	// Terminate the IdP session to prevent auto-login on next visit.
	// Use a static origin URL so it can be whitelisted in the IdP's allowed logout URLs.
	const samlIdpLogoutUrl = config.auth.samlIdpLogoutUrl;
	if (samlIdpLogoutUrl && config.auth.providerId === 'saml') {
		const logoutUrl = new URL(samlIdpLogoutUrl);
		logoutUrl.searchParams.set('returnTo', `${window.location.origin}/editor`);
		window.location.replace(logoutUrl.toString());
	} else if (config.auth.auth0Domain) {
		const logoutUrl = new URL(`https://${config.auth.auth0Domain}/v2/logout`);
		logoutUrl.searchParams.set('returnTo', `${window.location.origin}/editor`);
		if (config.auth.auth0ClientId) {
			logoutUrl.searchParams.set('client_id', config.auth.auth0ClientId);
		}
		window.location.replace(logoutUrl.toString());
	} else if (returnUrl) {
		window.location.href = returnUrl;
	} else {
		window.location.reload();
	}
}

/**
 * User type that works for both Auth0 and better-auth.
 * Includes common fields needed by the application.
 */
export interface AuthUser {
	id?: string;
	sub?: string;
	email?: string;
	email_verified?: boolean;
	name?: string;
	picture?: string | null;
	// Optional Auth0-specific fields that may be present
	given_name?: string;
	family_name?: string;
	nickname?: string;
	locale?: string;
	updated_at?: string;
	[key: string]: unknown; // Allow additional properties
}

/**
 * Fetches the current user.
 * @returns A promise which resolves with the user.
 */
export async function getUser(): Promise<AuthUser | undefined> {
	const betterAuthClient = getAuthClient();

	const session = await betterAuthClient.getSession();
	if (session.data?.user == null) {
		return;
	}
	// Return user in a format compatible with Auth0 user shape
	return {
		id: session.data.user.id,
		sub: session.data.user.id,
		email: session.data.user.email,
		email_verified: session.data.user.emailVerified ?? false,
		name: session.data.user.name,
		picture: session.data.user.image,
	};
}
